import React, { useCallback, useMemo, useState } from 'react';
import {
  SearchTree,
  SearchTreeItem,
  SearchField,
  Button,
} from '@ihsm/ui-shared';
import { useHistory } from 'react-router-dom';
import { REPORTS, REPORTS_NEW_REPORT } from 'routes';
import { useActions, useAppSelector } from 'state/store';
import './SearchTreeReports.scss';

interface SearchTreeReportsProps {
  disableAddButton?: boolean;
  showConfirmExitModal?: boolean;
  setModalVisible?: (visibility: boolean) => void;
}

export const SearchTreeReports: React.FC<SearchTreeReportsProps> = ({
  disableAddButton = false,
  showConfirmExitModal = false,
  setModalVisible = () => {},
}) => {
  const [filter, setFilter] = useState<string>();
  const { isSearchTreeCollapsed } = useAppSelector((state) => state.uiState);
  const { toggleSearchTree } = useActions();
  const { push } = useHistory();
  const { setSelectedReportId } = useActions();
  const { reports: reportsList, selectedReportID } = useAppSelector(
    (state) => state.customReportDataState
  );

  const handleAddNew = useCallback(() => {
    push(REPORTS_NEW_REPORT);
  }, [push]);

  const handleChange = useCallback(
    (id: string | number) => {
      if (showConfirmExitModal) {
        setModalVisible(true);
      } else {
        setSelectedReportId(id as string);
        push(REPORTS);
      }
    },
    [push, setModalVisible, setSelectedReportId, showConfirmExitModal]
  );

  const searchList: SearchTreeItem[] = useMemo(
    () =>
      reportsList
        .map((report) => ({
          id: report.reportConfigurationId,
          title: report.name,
          date: new Date(report.timestamp),
          badge: report.type !== 'Custom' ? report.type : undefined,
        }))
        .sort((a, b) => b.date.getTime() - a.date.getTime()),
    [reportsList]
  );

  const filteredList = useMemo(() => {
    if (filter && filter !== '') {
      const updatedList = [...searchList].filter((listItem) => {
        const filterClean = filter?.toLowerCase().trim() || '';

        const titleClean = listItem?.title?.toLowerCase().trim();
        const titleFound = titleClean?.indexOf(filterClean) !== -1;

        const badgeClean = listItem?.badge?.toLocaleLowerCase().trim();
        const badgeFound = badgeClean
          ? badgeClean?.indexOf(filterClean) !== -1
          : false;

        return titleFound || badgeFound;
      });

      return updatedList;
    }

    return searchList;
  }, [filter, searchList]);

  return (
    <div className="search-tree-report">
      <SearchTree
        header={
          <div className="search-tree-report__header">
            <SearchField delay={1000} onSearch={(value) => setFilter(value)} />
            <Button
              text="+"
              onClick={handleAddNew}
              disabled={disableAddButton}
            />
          </div>
        }
        list={filteredList}
        selectedId={selectedReportID}
        onSelect={handleChange}
        isCollapsed={isSearchTreeCollapsed}
        toggleCollapsed={toggleSearchTree}
      />
    </div>
  );
};
